'use client';

import { WagtailPage } from '@/types';
import Button from '@components/Button';
import { Checkbox, Fieldset, Input, RadioGroup } from '@components/Form';
import Link from 'next/link';
import { useActionState, useId, useState } from 'react';

import RawHtml from '@components/RawHtml';
import Honeypot from '@components/Honeypot';
import { removeBaseUrl } from '@utils/url';
import Select from '@components/Form/Select';
import { submitForm, SubmitError, SubmitSuccess, SubmitFormValidationError } from './actions';

function isSuccess(value: unknown): value is SubmitSuccess {
  return (value as SubmitSuccess)?.success === true;
}

function isFailure(value: unknown): value is SubmitError {
  return (value as SubmitError)?.success === false;
}

type Props = {
  terms_of_service: WagtailPage;
  page_id: number;
  courses: string[];
  dates: string[];
  thank_you_text?: string;
};

function InhouseForm(props: Props) {
  const { thank_you_text, terms_of_service, page_id, courses, dates } = props;

  const submitFormWithPageId = submitForm.bind(null, page_id);
  const [state, action, isPending] = useActionState(submitFormWithPageId, null);

  const id = useId();
  const [alternativeAddress, setAlternativeAddress] = useState(
    () => (state as SubmitFormValidationError)?.rawData?.alternative_address?.toString() === 'checked',
  );

  return (
    <div className='container'>
      <form id={`form-${id}`} className='inhouse-form' action={action}>
        <Fieldset legend='Auswahl'>
          <Select
            name='course'
            id={`${id}-course`}
            items={courses.map(course => ({ text: course.replace('\r\n', ''), value: course.replace('\r\n', '') }))}
            ariaLabel='Hiermit melde ich mich verbindlich an zum'
            label='Hiermit melde ich mich verbindlich an zum'
            required
            error={(state as SubmitFormValidationError)?.errors?.properties?.course?.errors?.join(', ')}
          />
          <p>
            <small>
              **Die Anmeldung gilt unter dem Vorbehalt der Zulassung zum Master-Fernstudiengang „Applied IT Security“
              durch das Prüfungsamt der Fakultät für Informatik der Ruhr-Universität Bochum.
            </small>
          </p>
          <RadioGroup
            name='date'
            id={`${id}-date`}
            items={dates.map(date => ({ label: date.replace('\r\n', ''), value: date.replace('\r\n', '') }))}
            label='Bitte bestätige deinen gewünschten Starttermin'
            required
            error={(state as SubmitFormValidationError)?.errors?.properties?.date?.errors?.join(', ')}
          />
        </Fieldset>
        <Fieldset legend='Ihre Kontaktdaten'>
          <div className='inhouse-form__row'>
            <Select
              id={`${id}-salutation`}
              name='salutation'
              label='Anrede'
              ariaLabel='Anrede'
              items={[
                { text: 'keine Angabe', value: 'keine Angabe' },
                { text: 'Herr', value: 'Herr' },
                { text: 'Frau', value: 'Frau' },
              ]}
              error={(state as SubmitFormValidationError)?.errors?.properties?.salutation?.errors?.join(', ')}
            />
            <Input
              name='title'
              label='Titel'
              id={`${id}-title`}
              defaultValue={(state as SubmitFormValidationError)?.rawData?.title?.toString()}
            />
          </div>
          <div className='inhouse-form__row'>
            <Input
              name='first_name'
              label='Vorname'
              id={`${id}-first-name`}
              required
              error={(state as SubmitFormValidationError)?.errors?.properties?.first_name?.errors?.join(', ')}
              defaultValue={(state as SubmitFormValidationError)?.rawData?.first_name?.toString()}
            />
            <Input
              name='last_name'
              label='Nachname'
              id={`${id}-last-name`}
              required
              error={(state as SubmitFormValidationError)?.errors?.properties?.last_name?.errors?.join(', ')}
              defaultValue={(state as SubmitFormValidationError)?.rawData?.last_name?.toString()}
            />
          </div>
        </Fieldset>
        <Fieldset legend='Adressdaten'>
          <div className='inhouse-form__row'>
            <Input
              name='company'
              label='Firma'
              id={`${id}-company`}
              error={(state as SubmitFormValidationError)?.errors?.properties?.company?.errors?.join(', ')}
              defaultValue={(state as SubmitFormValidationError)?.rawData?.company?.toString()}
            />
            <Input
              name='occupation'
              label='Tätigkeit'
              id={`${id}-occupation`}
              error={(state as SubmitFormValidationError)?.errors?.properties?.occupation?.errors?.join(', ')}
              defaultValue={(state as SubmitFormValidationError)?.rawData?.occupation?.toString()}
            />
          </div>
          <div className='inhouse-form__row'>
            <Input
              name='street'
              label='Straße'
              id={`${id}-street`}
              required
              error={(state as SubmitFormValidationError)?.errors?.properties?.street?.errors?.join(', ')}
              defaultValue={(state as SubmitFormValidationError)?.rawData?.street?.toString()}
            />
          </div>
          <div className='inhouse-form__row'>
            <Input
              name='zip'
              label='Postleitzahl'
              id={`${id}-zip`}
              required
              error={(state as SubmitFormValidationError)?.errors?.properties?.zip?.errors?.join(', ')}
              defaultValue={(state as SubmitFormValidationError)?.rawData?.zip?.toString()}
            />
            <Input
              name='city'
              label='Stadt'
              id={`${id}-city`}
              required
              error={(state as SubmitFormValidationError)?.errors?.properties?.city?.errors?.join(', ')}
              defaultValue={(state as SubmitFormValidationError)?.rawData?.city?.toString()}
            />
          </div>
          <Input
            name='country'
            label='Land'
            id={`${id}-country`}
            required
            error={(state as SubmitFormValidationError)?.errors?.properties?.country?.errors?.join(', ')}
            defaultValue={(state as SubmitFormValidationError)?.rawData?.first_name?.toString()}
          />
        </Fieldset>
        <Checkbox
          name='alternative_address'
          id={`${id}-alternative_address`}
          label='Abweichende Rechnungsadresse'
          checked={alternativeAddress}
          onCheckedChange={checked => setAlternativeAddress(Boolean(checked))}
        />
        <Fieldset legend='Abweichende Rechnungsadresse' className='inhouse-form__alternative-address'>
          <div className='inhouse-form__row'>
            <Input
              name='alternative_company'
              label='Firma'
              id={`${id}-company`}
              error={(state as SubmitFormValidationError)?.errors?.properties?.alternative_company?.errors?.join(', ')}
              defaultValue={(state as SubmitFormValidationError)?.rawData?.alternative_company?.toString()}
            />
          </div>
          <div className='inhouse-form__row'>
            <Input
              name='alternative_street'
              label='Straße'
              id={`${id}-street`}
              required={alternativeAddress}
              error={(state as SubmitFormValidationError)?.errors?.properties?.alternative_street?.errors?.join(', ')}
              defaultValue={(state as SubmitFormValidationError)?.rawData?.alternative_street?.toString()}
            />
          </div>
          <div className='inhouse-form__row'>
            <Input
              name='alternative_zip'
              label='Postleitzahl'
              id={`${id}-zip`}
              required={alternativeAddress}
              error={(state as SubmitFormValidationError)?.errors?.properties?.alternative_zip?.errors?.join(', ')}
              defaultValue={(state as SubmitFormValidationError)?.rawData?.zip?.toString()}
            />
            <Input
              name='alternative_city'
              label='Stadt'
              id={`${id}-city`}
              required={alternativeAddress}
              error={(state as SubmitFormValidationError)?.errors?.properties?.alternative_city?.errors?.join(', ')}
              defaultValue={(state as SubmitFormValidationError)?.rawData?.city?.toString()}
            />
          </div>
        </Fieldset>
        <Fieldset legend='Erreichbarkeit'>
          <Input
            name='telephone'
            label='Telefon'
            id={`${id}-telephone`}
            required
            defaultValue={(state as SubmitFormValidationError)?.rawData?.telephone?.toString()}
          />
          <div className='inhouse-form__row'>
            <Input
              name='email'
              type='email'
              label='E-Mail'
              id={`${id}-email`}
              required
              error={(state as SubmitFormValidationError)?.errors?.properties?.email?.errors?.join(', ')}
              defaultValue={(state as SubmitFormValidationError)?.rawData?.email?.toString()}
            />
            <Input
              name='email_confirmation'
              type='email'
              id={`${id}-email-confirmation`}
              required
              label='Bitte bestätige Deine E-Mail-Adresse'
              error={(state as SubmitFormValidationError)?.errors?.properties?.email_confirmation?.errors?.join(', ')}
              defaultValue={(state as SubmitFormValidationError)?.rawData?.email_confirmation?.toString()}
            />
          </div>
        </Fieldset>
        <Select
          id={`${id}-acquisition`}
          name='acquisition'
          label='Wie bist Du auf uns aufmerksam geworden?'
          ariaLabel='Wie bist Du auf uns aufmerksam geworden?'
          defaultValue={(state as SubmitFormValidationError)?.rawData?.acquisition?.toString()}
          items={[
            {
              value: 'Printmedien',
              text: 'Printmedien',
            },
            {
              value: 'Messe/Veranstaltung',
              text: 'Messe/Veranstaltung',
            },
            {
              value: 'Newsletter',
              text: 'Newsletter',
            },
            {
              value: 'Empfehlung',
              text: 'Empfehlung',
            },
            {
              value: 'Social Media',
              text: 'Social Media',
            },
            {
              value: 'Suchmaschinen-Suche',
              text: 'Suchmaschinen-Suche',
            },
            {
              value: 'Schulungs-Portal',
              text: 'Schulungs-Portal',
            },
            {
              value: 'Fernstudium-direkt.de',
              text: 'Fernstudium-direkt.de',
            },
            {
              value: 'FernstudiumCheck.de',
              text: 'FernstudiumCheck.de',
            },
            {
              value: 'Anderes Portal',
              text: 'Anderes Portal',
            },
          ]}
        />
        <div className='inhouse-form__terms-of-service'>
          <Checkbox
            id={`${id}-tos`}
            name='terms_of_service'
            label={
              <>
                Ich habe die{' '}
                <Link href={removeBaseUrl(terms_of_service.full_url)} target='_blank' className='inhouse-form__link'>
                  Teilnahmebedingungen
                </Link>{' '}
                zur Kenntnis genommen.
              </>
            }
            defaultChecked={(state as SubmitFormValidationError)?.rawData?.email_confirmation?.toString() === 'checked'}
            required
          />
        </div>
        {isSuccess(state) ? (
          <div className='message message--success'>
            <RawHtml html={thank_you_text} />
          </div>
        ) : null}
        {isFailure(state) ? (
          <div className='message message--error'>
            <p>Leider ist etwas schief gegangen... Bitte versuchen Sie es später erneut...</p>
            {process.env?.NEXT_PUBLIC_CONTACT_EMAIL ? (
              <p>
                Alternativ können Sie Ihre Anfrage auch direkt an die E-Mail-Adresse{' '}
                <a href={`mailto:${process.env.NEXT_PUBLIC_CONTACT_EMAIL}`}>{process.env.NEXT_PUBLIC_CONTACT_EMAIL}</a>{' '}
                senden.
              </p>
            ) : null}
          </div>
        ) : null}
        <Button primary type='submit' disabled={isPending || isSuccess(state)}>
          Anmeldung absenden
        </Button>
        <Honeypot />
      </form>
    </div>
  );
}

export default InhouseForm;
